import { useCallback, useMemo } from "react";
import { useApolloClient, useQuery } from "@apollo/client/react";
import {
  Box,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type EntityItemConfig,
  type EntityPoolConfig,
  type IconName,
  type Omittable,
} from "@salestrak/ui";
import {
  GlobalSearchIssuesDocument,
  type IssuePoolRow,
  type IssueWhereInput,
} from "#app/lib/data";
import { shortAddress } from "#app/lib/utils/format-address";
import {
  entityFromAssigneeRef,
  getEntityGlyph,
  type EntityData,
} from "#app/lib/entity-visuals";
import { AgeLabel } from "#app/components/age/age-label";
import { EntityGroupTag, EntityTag } from "#app/components/entity";
import { hasActiveAge } from "#app/lib/age-status";
import {
  useWorkspaceData,
  type WorkspaceStage,
} from "#app/lib/data/workspace-data-provider";
import { pluralize } from "#app/lib/utils/pluralize";
import { poolItemVisual } from "../utils";
import { useEntitySearchPool } from "./use-entity-search-pool";

const POOL_ID = "issues";
/** The server page the browse query and a search each ask for. A picker that
 *  renders a full page heads it "Top N matches" rather than implying the count
 *  is exhaustive — pass this as the picker's `pageSize`. */
export const ISSUE_POOL_PAGE_SIZE = 25;
const INITIAL_QUERY_VARS = { limit: ISSUE_POOL_PAGE_SIZE };

/**
 * The row's leading stage fact: glyph only, so the project name is the first
 * text the eye lands on. The tooltip carries what the glyph cannot — the stage
 * name, and the workflow it belongs to when known (two workflows can each have
 * a "Signed"). Non-interactive: the row's press is the selection toggle.
 */
function StageRowFact({
  stage,
  workflowName,
}: Omittable<{ stage: WorkspaceStage; workflowName?: string }>) {
  return (
    <Tooltip>
      <TooltipTrigger render={<Box direction="row" align="center" />}>
        <EntityTag
          display="visual"
          entity={{ id: stage.stageId, kind: "stage", stage }}
          interactive={false}
          size="xs"
        />
      </TooltipTrigger>
      <TooltipPopup side="top">
        <Box gap="2xs">
          <Text size="xs">{stage.name}</Text>
          {workflowName ? <Text size="2xs">{workflowName} workflow</Text> : null}
        </Box>
      </TooltipPopup>
    </Tooltip>
  );
}

/** Resolved from the workspace topology already in memory — a pool row carries
 *  only the ids. */
export type IssuePoolTopology = Omittable<{
  stage?: WorkspaceStage;
  workflowName?: string;
}>;

// Issue/project → selector-item mapping. Address rides along as the row subtitle
// and a search keyword, so one box finds a project by name OR address (server
// `search` matches both). icon/workflowId are kept as data for round-tripping.
// The row's slots carry the facts that pick a project APART from its namesakes
// — stage leading, assignees + age trailing.
export function issueToPoolItem(
  node: IssuePoolRow,
  topology?: IssuePoolTopology,
): EntityItemConfig {
  const entity: EntityData = {
    id: node.issueId,
    issue: { icon: node.icon, name: node.name },
    kind: "issue",
    workflowId: node.workflowId,
  };
  const glyph = getEntityGlyph(entity);
  const address = node.address?.wholeAddress?.trim() || undefined;
  const short = address ? shortAddress(address) : undefined;
  const rawTitle = node.name.trim();
  // An address-named project would print its address twice — the (shortened)
  // address becomes the title and the subtitle drops.
  const isAddressTitle = !!rawTitle && rawTitle === address;
  const title = (isAddressTitle ? short : rawTitle) || "Untitled Project";
  const description = address && !isAddressTitle ? short : undefined;
  const assignees = (node.assignees ?? []).map(entityFromAssigneeRef);
  // A "disabled" age is a project whose stage doesn't clock — a grey clock on
  // every such row is noise, so the fact drops instead of rendering empty.
  const age = hasActiveAge(node.age) ? node.age : undefined;
  const { stage, workflowName } = topology ?? {};
  return {
    avatar: {
      color: glyph.color ?? undefined,
      icon: (glyph.icon ?? undefined) as IconName,
      padded: true,
      render: () => poolItemVisual(entity),
    },
    data: {
      entityType: "issue",
      wholeAddress: address ?? null,
      workflowId: node.workflowId,
    },
    description,
    id: node.issueId,
    keywords: address ? [address] : undefined,
    ...(stage
      ? {
          rowLead: () => (
            <StageRowFact stage={stage} workflowName={workflowName} />
          ),
        }
      : {}),
    ...(assignees.length > 0 || age
      ? {
          rowMeta: () => (
            <>
              {assignees.length > 0 ? (
                <EntityGroupTag
                  entities={assignees}
                  // No nested hover CARD inside a picker row: the row is the
                  // hover target, and a card opening off an avatar is the
                  // popover these inline facts exist to be rid of. The plain
                  // tooltip is the replacement — it names everyone, including
                  // whoever the "+N" is hiding.
                  hoverDisabled
                  interactive={false}
                  max={3}
                  presence={false}
                  size="xs"
                  surface="card"
                  title={pluralize("Assignee", { count: assignees.length })}
                  tooltip
                  variant="stack"
                />
              ) : null}
              {/* The colored clock is a status the row never spells out. */}
              {age ? (
                <AgeLabel age={age} size="xs" stageId={node.stageId} tooltip />
              ) : null}
            </>
          ),
        }
      : {}),
    title,
  } satisfies EntityItemConfig;
}

export type UseIssuePoolOptions = Omittable<{
  /** Issue IDs to exclude from the pool. */
  exclude?: string[];
  /** Pass a stable (memoized) value: it is part of the query variables. */
  where?: IssueWhereInput;
}>;

/**
 * Fetches recent projects (issues) and returns an `EntityPoolConfig` for use with
 * `EntitySelector`. Server-side search via `GlobalSearchIssues` matches name AND
 * address, so the same box finds a project by either.
 */
export function useIssuePool(options?: UseIssuePoolOptions) {
  const { exclude, where } = options ?? {};
  const client = useApolloClient();
  // The row's stage glyph and its workflow tooltip resolve from the workspace
  // topology already in memory — a pool row carries only the ids.
  const { stageMap, workflowMap } = useWorkspaceData();

  const initialVars = useMemo(
    () => (where ? { ...INITIAL_QUERY_VARS, where } : INITIAL_QUERY_VARS),
    [where],
  );
  const { data, loading } = useQuery(GlobalSearchIssuesDocument, {
    variables: initialVars,
  });

  const toItem = useCallback(
    (node: IssuePoolRow) => {
      const stage = stageMap.get(node.stageId);
      const workflowName = workflowMap.get(node.workflowId)?.name;
      return issueToPoolItem(node, {
        ...(stage ? { stage } : {}),
        ...(workflowName ? { workflowName } : {}),
      });
    },
    [stageMap, workflowMap],
  );

  const initialItems = useMemo<EntityItemConfig[]>(() => {
    const edges = data?.issues?.edges ?? [];
    return edges.filter((edge) => edge?.node).map((edge) => toItem(edge.node));
  }, [data, toItem]);

  const runSearch = useCallback(
    async (trimmed: string): Promise<EntityItemConfig[]> => {
      const { data: result } = await client.query({
        query: GlobalSearchIssuesDocument,
        variables: { limit: ISSUE_POOL_PAGE_SIZE, where: { ...where, search: trimmed } },
        fetchPolicy: "no-cache",
      });
      const edges = result?.issues?.edges ?? [];
      return edges
        .filter((edge: { node?: unknown } | null) => edge?.node)
        .map((edge: { node: IssuePoolRow }) => toItem(edge.node));
    },
    [client, toItem, where],
  );

  const { items, onSearchChange } = useEntitySearchPool({
    baseItems: initialItems,
    runSearch,
    exclude,
    epoch: initialVars,
  });

  const pool = useMemo<EntityPoolConfig>(
    () => ({
      // Every project row carries the same box glyph — a column of identical
      // icons is noise, so the pool gives the leading rail to the stage
      // instead (`rowLead`), which differs row to row. The avatar config stays
      // on each item: the trigger and the chips render the project's own
      // visual once it is picked.
      hideAvatars: true,
      id: POOL_ID,
      items,
      label: "Projects",
      // Unfetched, the page is empty the same way an exhausted one is; the
      // flag is what keeps the empty state from claiming "nothing left" while
      // the first page is still in flight.
      loading,
      onSearchChange,
      serverFiltered: true,
    }),
    [items, loading, onSearchChange],
  );

  return { pool, loading };
}

export { POOL_ID as ISSUE_POOL_ID };
